import React from 'react';
import { useNavigate } from 'react-router-dom';

const TASKS = [
  {
    level: 'urgent' as const,
    title: 'SYN-005 SAE Report',
    description: '15-day window closes in 6 hours',
    detail: 'SUSAR \u2014 FDA + investigators must be notified immediately',
    button: '\u2192 Go to SAE Clock',
    route: '/sae',
  },
  {
    level: 'amber' as const,
    title: 'SYN-002 Visit 3 \u2014 Data Entry Due',
    description: 'Required: Vital signs, PK sample, CBC labs',
    detail: 'Last entry 2 days ago',
    button: '\u2192 View Patient',
    route: '/safety',
  },
  {
    level: 'amber' as const,
    title: 'SYN-007 Visit 2 \u2014 Data Entry Due',
    description: 'Required: Vital signs, physical exam',
    detail: 'Scheduled today per protocol',
    button: '\u2192 View Patient',
    route: '/safety',
  },
  {
    level: 'yellow' as const,
    title: 'SYN-004 Re-consent Required',
    description: 'Protocol Amendment 1',
    detail: 'Patient not yet re-consented to updated protocol version 2/1/2026',
    button: '\u2192 View Consent',
    route: '/safety',
  },
  {
    level: 'green' as const,
    title: 'SYN-001 Visit 3 \u2014 Complete',
    description: 'All data entered',
    detail: 'SDV pending monitor review',
    button: '\u2192 View Record',
    route: '/evidence',
  },
];

const LEVEL_STYLES: Record<string, { border: string; dot: string; label: string; bg: string }> = {
  urgent: { border: '#f56565', dot: '#f56565', label: 'URGENT', bg: '#742a2a' },
  amber:  { border: '#ed8936', dot: '#ed8936', label: 'DUE',    bg: '#744210' },
  yellow: { border: '#ecc94b', dot: '#ecc94b', label: 'ACTION', bg: '#744210' },
  green:  { border: '#48bb78', dot: '#48bb78', label: 'DONE',   bg: '#22543d' },
};

const ALERTS = [
  { type: 'warning' as const, text: 'sp-004 temperature excursion', detail: 'Blood PK sample rejected \u2014 chain broken', action: 'Reschedule PK draw' },
  { type: 'warning' as const, text: 'SYN-004 re-consent pending', detail: 'Amendment 1 issued 2/1/2026', action: '' },
  { type: 'info' as const, text: 'ev-011 recording delay 23h', detail: 'Lab result entered 23 hours late', action: '' },
];

const QUICK_ACTIONS = [
  { label: 'Patient List',      route: '/safety' },
  { label: 'Sample Log',        route: '/samples' },
  { label: 'Report AE',         route: '/deviations' },
  { label: 'Safety Dashboard',  route: '/safety' },
];

export default function HomeCoordinator() {
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.headerRow}>
        <div>
          <h1 style={s.greeting}>Good morning &mdash; Site Coordinator</h1>
          <p style={s.trialId}>DEMO-001 &middot; Sym004 Phase 1b/2a</p>
          <p style={s.date}>{today}</p>
        </div>
        <button style={s.roleBtn} onClick={() => navigate('/role-select')}>
          Switch Role
        </button>
      </div>

      {/* Today's Tasks */}
      <h2 style={s.sectionTitle}>Today&rsquo;s Tasks</h2>
      <div style={s.taskList}>
        {TASKS.map((t, i) => {
          const ls = LEVEL_STYLES[t.level];
          return (
            <div key={i} style={{ ...s.taskCard, borderLeftColor: ls.border }}>
              <div style={s.taskHeader}>
                <span style={{ ...s.levelBadge, backgroundColor: ls.bg, color: ls.dot }}>{ls.label}</span>
                <span style={s.taskTitle}>{t.title}</span>
              </div>
              <p style={s.taskDesc}>{t.description}</p>
              <p style={s.taskDetail}>{t.detail}</p>
              <button style={{ ...s.taskBtn, color: ls.border }} onClick={() => navigate(t.route)}>
                {t.button}
              </button>
            </div>
          );
        })}
      </div>

      {/* Recent Alerts */}
      <h2 style={s.sectionTitle}>Recent Alerts</h2>
      <div style={s.alertList}>
        {ALERTS.map((a, i) => (
          <div key={i} style={{ ...s.alertCard, borderLeftColor: a.type === 'warning' ? '#ed8936' : '#4299e1' }}>
            <div style={s.alertHeader}>
              <span style={{ fontSize: 14 }}>{a.type === 'warning' ? '\u26A0\uFE0F' : '\u2139\uFE0F'}</span>
              <span style={s.alertText}>{a.text}</span>
            </div>
            <p style={s.alertDetail}>{a.detail}</p>
            {a.action && <p style={{ ...s.alertDetail, color: '#ed8936', fontWeight: 600 }}>Action: {a.action}</p>}
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <h2 style={s.sectionTitle}>Quick Actions</h2>
      <div style={s.quickRow}>
        {QUICK_ACTIONS.map((qa, i) => (
          <button key={i} style={s.quickBtn} onClick={() => navigate(qa.route)}>
            {qa.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { padding: 32, maxWidth: 900 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 },
  greeting: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', margin: 0 },
  trialId: { fontSize: 14, color: '#63b3ed', marginTop: 4 },
  date: { fontSize: 13, color: '#718096', marginTop: 2 },
  roleBtn: {
    padding: '8px 16px', borderRadius: 6, border: '1px solid #2d3748',
    backgroundColor: '#171923', color: '#a0aec0', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#cbd5e0', marginBottom: 12, marginTop: 28, textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  taskList: { display: 'flex', flexDirection: 'column' as const, gap: 10 },
  taskCard: {
    backgroundColor: '#1a202c', border: '1px solid #2d3748', borderLeft: '4px solid',
    borderRadius: 8, padding: '14px 18px',
  },
  taskHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
  levelBadge: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12, letterSpacing: 0.5 },
  taskTitle: { fontSize: 14, fontWeight: 600, color: '#e2e8f0' },
  taskDesc: { fontSize: 13, color: '#cbd5e0', margin: '0 0 4px 0' },
  taskDetail: { fontSize: 12, color: '#718096', margin: '0 0 10px 0' },
  taskBtn: {
    background: 'none', border: 'none', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', padding: 0, transition: 'opacity 0.15s',
  },
  alertList: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  alertCard: {
    backgroundColor: '#1a202c', border: '1px solid #2d3748', borderLeft: '4px solid',
    borderRadius: 8, padding: '10px 16px',
  },
  alertHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  alertText: { fontSize: 13, fontWeight: 600, color: '#e2e8f0' },
  alertDetail: { fontSize: 12, color: '#a0aec0', margin: '0 0 2px 0' },
  quickRow: { display: 'flex', gap: 12, flexWrap: 'wrap' as const },
  quickBtn: {
    padding: '10px 20px', borderRadius: 8, border: '1px solid #2d3748',
    backgroundColor: '#1a202c', color: '#63b3ed', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },
};
